import { useState, useEffect, useContext } from 'react'
import EnvContext from '../context/EnvContext'

import { gql, useQuery } from '@apollo/client';
import { ONE_MINUTE, THREE_MINUTES } from './pollIntervals';


const CONNECTION_QUERY_NARROW = gql`
query ($envName: String!, $connectionList: [bigint!]) {
    connection(where: {connectionType: {environment: {name: {_eq: $envName}}}, id: {_in: $connectionList}}) {
      id
      name
      exchangeId
      accounts
      connectionInfo
      companyName
      datetimelastup
      servers
      enabled
      enabledByOwner
      enabledByTT
      connectionType {
        displayName
        marketId
        typeId
        typeName
        environment {
          name
        }
      }
      priHost: connectionInfo(path: "host")
      priPort: connectionInfo(path: "port")
      secHost: connectionInfo(path: "secHost")
      secPort: connectionInfo(path: "secPort")
    }
  }  
`

const CONNECTION_QUERY = gql`
query ($envName: String!) {
    connection(where: {connectionType: {environment: {name: {_eq: $envName}}}}) {
      id
      name
      exchangeId
      accounts
      connectionInfo
      companyName
      datetimelastup
      servers
      enabled
      enabledByOwner
      enabledByTT
      connectionType {
        displayName
        marketId
        typeId
        typeName
        environment {
          name
        }
      }
      priHost: connectionInfo(path: "host")
      priPort: connectionInfo(path: "port")
      secHost: connectionInfo(path: "secHost")
      secPort: connectionInfo(path: "secPort")
    }
  }  
`

const ACTIVE_CONNECTION_QUERY = gql`
query($envName: String!) {
    service_node(
        where: {
            node: {
                environment: {
                    name: {_eq: $envName }
                }
            }, 
            sessionType: {_eq: "connections"}
        }
    ) {
    sessionData
    instance
    node {
        name
    }
    ocInstanceId: zookeeper(path: "custom_data.dedicated_instance_id")
    ocSchedule: zookeeper(path: "custom_data.oc_information.schedule.status")
    }
}
`

const CONNECTION_ACCT_QUERY = gql`
query($envName: String!) {
    connection(
        where: {
            connectionType: {
                environment: {
                    name: {_eq: $envName }
                }
            }
        }
    ){
        id
        accounts
    }
}
`

const OC_INFO = gql`
query ConnectionsQuery($envName: String!) {
    service_node(where: {node: {environment: {name: {_eq: $envName}}}, sessionType: {_eq: "connections"}}) {
      ocInstanceId: zookeeper(path: "custom_data.dedicated_instance_id")
      instance
      node {
        name
      }
    }
  }  
`

export const useConnection = (env = "", connectionList = [], wait_for_connectionList = false) => {

    const envSelected = useContext(EnvContext)
    const [lookupData, setLookupData] = useState({})
    const { data, loading } = useQuery(connectionList.length > 0 ? CONNECTION_QUERY_NARROW : CONNECTION_QUERY,
        {
            variables: connectionList.length > 0 ?
                {
                    envName: env ? env : envSelected,
                    connectionList: connectionList
                } :
                {
                    envName: env ? env : envSelected,
                },
            pollInterval: connectionList.length < 200 ? ONE_MINUTE : THREE_MINUTES,
            skip: wait_for_connectionList && connectionList.length === 0 ? true : false
        }
    )

    useEffect(() => {
        if (!loading && data) {
            setLookupData(data.connection.reduce((acc, d) => {
                if (d)
                    acc[d.id] = {
                        'id': d.id,
                        'name': d.name,
                        'exchangeId': d.exchangeId,
                        'accounts': d.accounts,
                        'companyName': d.companyName,
                        'connectionInfo': d.connectionInfo,
                        'datetimelastup': d.datetimelastup,
                        'connectionType': {
                            'displayName': d.connectionType.displayName,
                            'marketId': d.connectionType.marketId,
                            'typeName': d.connectionType.typeName,
                            'typeId': d.connectionType.typeId,
                        },
                        'environment': d.connectionType.environment.name,
                        'servers': d.servers,
                        'enabled': d.enabled,
                        'enabledByOwner': d.enabledByOwner,
                        'enabledByTT': d.enabledByTT
                    }

                if (d.priHost && d.priPort) {
                    acc[d.id]["primary"] = d.priHost + " / " + d.priPort
                }

                if (d.secHost && d.secPort) {
                    acc[d.id]["secondary"] = d.secHost + " / " + d.secPort
                }

                return acc
            }, {}))
        }
    }, [data, loading])

    return lookupData
}

export const useActiveConnection = () => {

    const envSelected = useContext(EnvContext)
    const [lookupData, setLookupData] = useState({})
    const { data, loading } = useQuery(ACTIVE_CONNECTION_QUERY, { variables: { envName: envSelected }, pollInterval: ONE_MINUTE })



    useEffect(() => {
        if (!loading && data) {
            setLookupData(data.service_node.reduce((sessionData, d) => {
                if (d)
                    sessionData[d.id] = {
                        'id': d.id,
                        'status': d.status,
                        'session': d.session,
                        'type_id': d.type_id,
                        'connectionInfo': d.connectionInfo,
                        'market_id': d.market_id,
                        'node': {
                            'name': d.node.name,
                        },
                        'ocInstanceId': d.ocInstanceId,
                        'ocSchedule': d.ocSchedule,
                    }
                return sessionData
            }, {}))
        }
    }, [data, loading])

    return lookupData
}

export const useConnectionAccount = () => {

    const envSelected = useContext(EnvContext)
    const [lookupData, setLookupData] = useState({})
    const { data, loading } = useQuery(CONNECTION_ACCT_QUERY, { variables: { envName: envSelected }, pollInterval: ONE_MINUTE })




    useEffect(() => {
        if (!loading && data) {
            let connection_accounts = {}
            data.connection.forEach(connection => {
                connection.accounts.forEach(account => {
                    if (connection_accounts[account]) {
                        let newArr = connection_accounts[account]
                        newArr.push(connection.id)
                        connection_accounts[account] = newArr
                    } else {
                        connection_accounts[account] = [connection.id]
                    }
                });
            });
            setLookupData(connection_accounts)
        }
    }, [data, loading])

    return lookupData
}

export const useConnectionOCInfo = (env = "") => {

    const envSelected = useContext(EnvContext)
    const [lookupData, setLookupData] = useState({})
    const { data, loading } = useQuery(OC_INFO, { variables: { envName: env ? env : envSelected }, pollInterval: ONE_MINUTE })



    useEffect(() => {
        if (!loading && data) {
            setLookupData(data.service_node.reduce((acc, d) => {
                if (d)
                    acc[d.node.name] = {
                        'name': d.node.name,
                        'ocInstanceId': d.ocInstanceId,
                        'instance': d.instance,

                    }
                return acc
            }, {}))
        }
    }, [data, loading])

    return lookupData
}

export const useLiveOCData = (env = "") => {

    const OC_LIVE_DATA = gql`
    query ($envName: String!) {
        sessionOCNoCompanyView (
          where: {
            environmentName: {_eq: $envName},
          },
        ) {
            datacenterId
            datacenterName
            environmentName
            nodeId
            nodeName
            instance
            connectionId
            marketId
            session
            sessions
            status
            nodeIp
          }
    }
    `

    const envSelected = useContext(EnvContext)
    const [lookupData, setLookupData] = useState({})
    const { data, loading } = useQuery(OC_LIVE_DATA, { variables: { envName: env ? env : envSelected }, pollInterval: ONE_MINUTE })

    useEffect(() => {
        if (!loading && data) {
            setLookupData(data.sessionOCNoCompanyView.reduce((acc, d) => {
                if (d)
                    acc[d.connectionId] = {
                        'connectionId': d.connectionId,
                        'instance': d.instance,
                        'session': d.session,
                        'sessions': d.sessions,
                        'nodeName': d.nodeName,
                        'status': d.status
                    }
                return acc
            }, {}))
        }
    }, [data, loading])

    return lookupData
}
